import {IBizSign, IBusiness} from "@/views/editor/service/business";
import {AEditor} from "@/views/editor/service/editor";
import {IActivityBusinessMeta} from "@/model/activity";
import {IInput} from "@/views/editor/service/types";
import {ASign} from "@/views/editor/service/sign/sign";
import {CheckboxOptionType} from "ant-design-vue/es/checkbox/interface";
import {h, reactive, VNode, watch} from "vue";
import {Rule} from "ant-design-vue/es/form";
import {Button, Flex, Form, FormItem, Input, InputNumber, Segmented, Switch} from "ant-design-vue";
import {IFormState} from "@/lib/types";
import {Drag} from "@/lib/drag";
import {DeleteOutlined, MenuOutlined, PlusCircleOutlined} from "@ant-design/icons-vue";

export interface ISignMeta {
    inputs: IInput[]
    custom_ad: {
        type: 'system' | 'custom' | 'none'
        enter_text: string
        slogan: string
        contact: string
        phone_number: string
        qr_image_url: string
    }
    props: {
        is_auto_phone_number: boolean
        button_text: string
    }
}


export default class Sign extends ASign implements IBizSign {
    readonly opt: ISignMeta
    active = reactive({
        input: {} as IInput,
        rules: {} as Record<string, Rule[]>,
        fs: {} as IFormState
    })
    readonly typeName: { [key: string]: string } = {
        'input': '文本项',
        'radio': '单选项',
        'checkbox': '多选项',
        'contact': '姓名项',
        'phone_number': '手机号',
        'role': '身份',
    }

    constructor(protected readonly editor: AEditor, readonly meta: IActivityBusinessMeta) {
        super(editor, meta);
        this.opt = this.meta.setting as ISignMeta
        this.active.input = this.emptyInput('input')
        this.active.rules = {
            title: [{
                required: true,
                validator: () => this.active.input.title ? Promise.resolve() : Promise.reject('信息项名称不能为空')
            }],
            input_type: [{
                required: ['input'].includes(this.active.input.type),
                validator: () => (['input'].includes(this.active.input.type) && !this.active.input.input_type) ? Promise.reject('信息项名称不能为空') : Promise.resolve()
            }],
            items: [{
                required: ['radio', 'checkbox'].includes(this.active.input.type),
                validator: () => !['radio', 'checkbox'].includes(this.active.input.type) ? Promise.resolve() : this.active.input.items.length == 0 ? Promise.reject('至少要有1个选项') : Promise.resolve()
            }],
            max: [{
                required: ['checkbox'].includes(this.active.input.type),
                validator: () => !['checkbox'].includes(this.active.input.type) ? Promise.resolve() : this.active.input.max <= 0 ? Promise.reject('多选项最多选择项数必须大于0') : Promise.resolve()
            }],
            min: [{
                required: ['checkbox'].includes(this.active.input.type),
                validator: () => !['checkbox'].includes(this.active.input.type) ? Promise.resolve() : this.active.input.min <= 0 ? Promise.reject('多选项最少选择项数必须大于0') : Promise.resolve()
            }],
        }
        watch(() => this.active.input.items, () => {
            Object.keys(this.active.rules).filter(k => k.startsWith('items.')).forEach(k => delete this.active.rules[k])
            this.active.input.items.forEach((_, k) => {
                this.active.rules[`items.${k}`] = [{
                    required: true, validator: (_: unknown, v: string) => {
                        if (!v) return Promise.reject('选项不能为空')
                        else if (this.active.input.items.filter(i => i == v).length > 1) return Promise.reject('不能有重复选项')
                        return Promise.resolve()
                    }
                }]
            })
        }, {deep: true})
    }

    subs(): CheckboxOptionType[] {
        return []
    }

    resetMeta(): IBusiness {
        const opt: ISignMeta = {
            custom_ad: {
                contact: "获客专家张老师",
                slogan: "专业营销获客找管店宝",
                type: 'system',
                phone_number: "",
                qr_image_url: "",
                enter_text: "我也要免费创建拓客活动"
            },
            inputs: [
                {
                    origin: this.title,
                    id: 'contact',
                    required: true,
                    title: '联系人',
                    type: 'contact',
                    input_type: 'text',
                    deletable: false,
                    editable: true,
                    items: [], max: 0, min: 0,
                },
                {
                    origin: this.title,
                    id: 'phone_number',
                    required: true,
                    title: '手机号',
                    type: 'phone_number',
                    input_type: 'number',
                    deletable: false,
                    editable: false,
                    items: [], max: 0, min: 0,
                },
            ],
            props: {button_text: "", is_auto_phone_number: true}
        }
        Object.assign(this.opt, opt)
        return this;
    }

    setButtonText(text: string): void {
        this.opt.props.button_text = this.opt.props.button_text ? this.opt.props.button_text : text
    }

    getButtonText(): string {
        return this.opt.props.button_text
    }

    set(origin: string, inputs: IInput[]): void {
        if (this.opt.inputs.some(x => x.origin === origin)) {
            const l = this.opt.inputs.filter(x => x.origin !== origin);
            if (inputs.length > 0) l.push(...inputs)
            this.opt.inputs = l
        } else if (inputs.length > 0) this.opt.inputs.push(...inputs)
    }

    emptyInput(cate: 'input' | 'radio' | 'checkbox'): IInput {
        switch (cate) {
            case 'input':
                return {
                    items: [], max: 0, min: 0,
                    origin: this.title,
                    id: Math.random().toString(36).substring(2),
                    required: false,
                    title: '',
                    type: cate,
                    input_type: 'text',
                    deletable: true,
                    editable: true
                }
            case 'radio':
                return {
                    input_type: 'text', max: 0, min: 0,
                    origin: this.title,
                    id: Math.random().toString(36).substring(2),
                    required: false,
                    title: '',
                    type: cate,
                    items: ['',''],
                    deletable: true,
                    editable: true
                }
            case 'checkbox':
                return {
                    input_type: 'text',
                    origin: this.title,
                    id: Math.random().toString(36).substring(2),
                    required: false,
                    title: '',
                    type: cate,
                    max: 2,
                    min: 1,
                    items: ['',''],
                    deletable: true,
                    editable: true
                }
            default:
                throw new Error('不支持的类型')
        }
    }

    drag = new Drag();

    content(): VNode {

        this.drag.isDragEnabled = false
        switch (this.active.input.type) {
            case 'contact':
                return h(Form, {style: {paddingTop: '12px'}}, () => [h(FormItem, {
                        label: '名称',
                        labelCol: {span: 5},
                        style: {paddingTop: '12px'}, ...this.active.fs.validateInfos['title']
                    }, () => [h(Input, {
                        autocomplete: 'off',
                        placeholder: '填写你想获取谁的姓名，如“家长”“孩子”',
                        maxlength: 15,
                        value: this.active.input.title,
                        "onUpdate:value": v => this.active.input.title = v
                    })]
                )])
            case 'role':
                return h(Form, {style: {paddingTop: '12px'}}, () => [
                    h(FormItem, {label: '新客', ...this.active.fs.validateInfos['items.0']}, () =>
                        h(Input, {
                            autocomplete: 'off',
                            maxlength: 8,
                            placeholder: '填写新客称呼', value: this.active.input.items[0],
                            "onUpdate:value": v => this.active.input.items[0] = v
                        })
                    ),
                    h(FormItem, {label: '老客', labelCol: {span: 5}, ...this.active.fs.validateInfos['items.1']}, () =>
                        h(Input, {
                            autocomplete: 'off',
                            maxlength: 8,
                            placeholder: '填写老客称呼', value: this.active.input.items[1],
                            "onUpdate:value": v => this.active.input.items[1] = v
                        })
                    ),
                ])
            case 'input':
                return h(Form, {style: {paddingTop: '12px'}}, () => [
                    h(FormItem, {label: '必填'}, () =>
                        h(Flex, {gap: 'middle'}, () => [
                            h(Switch, {
                                checked: this.active.input.required,
                                checkedChildren: '是', unCheckedChildren: '否',
                                "onUpdate:checked": v => this.active.input.required = v as boolean
                            })
                        ])),
                    h(FormItem, {
                            label: '名称',
                            ...this.active.fs.validateInfos['title']
                        }, () => [h(Input, {
                            autocomplete: 'off',
                            placeholder: '填写你想要收集的信息',
                            maxlength: 15,
                            value: this.active.input.title,
                            "onUpdate:value": v => this.active.input.title = v
                        })]
                    ),
                    h(FormItem, {
                        label: '文本项模式',
                        ...this.active.fs.validateInfos['input_type']
                    }, () => [
                        h(Flex, {vertical: true, gap: 'middle'}, () => [
                            h('div',
                                h(Segmented, {
                                    value: this.active.input.input_type,
                                    options: [{label: '普通输入', value: 'text'},
                                        {label: '数字滚轮', value: 'number'}, {label: '日期选择', value: 'date'}],
                                    "onUpdate:value": v => this.active.input.input_type = v as 'text' | 'number' | 'date'
                                }),
                            ),
                            h('img', {
                                src: {
                                    text: require('@/assets/image/img_input_mode_example.gif'),
                                    number: require('@/assets/image/img_number_mode_example.gif'),
                                    date: require('@/assets/image/img_date_mode_example.gif'),
                                }[this.active.input.input_type],
                                style: {display: 'block', width: '240px'}
                            })
                        ])
                    ]),
                ])

            case 'radio':
            case 'checkbox':
                this.drag.isDragEnabled = false
                return h(Form, {style: {paddingTop: '12px', maxWidth: '560px'}}, () => [
                    h(FormItem, {label: '必填'}, () =>
                        h(Flex, {gap: 'middle', align: 'center'}, () => [
                            h(Switch, {
                                checked: this.active.input.required,
                                checkedChildren: '是', unCheckedChildren: '否',
                                "onUpdate:checked": v => this.active.input.required = v as boolean
                            }),
                            this.active.input.type == 'checkbox' ?
                                h(Flex, {align: 'center', gap: 'small'}, () => ['最少选择', h(InputNumber, {
                                    value: this.active.input.min,
                                    "onUpdate:value": v => this.active.input.min = v as number,
                                    min: 1,
                                    max: this.active.input.items.length - 1,
                                    style: {width: '60px'}
                                }), '项，最多选择', h(InputNumber, {
                                    value: this.active.input.max,
                                    "onUpdate:value": v => this.active.input.max = v as number,
                                    min: this.active.input.min,
                                    max: this.active.input.items.length,
                                    style: {width: '60px'}
                                }), '项']) : null
                        ])),
                    h(FormItem, {label: '名称', ...this.active.fs.validateInfos['title']}, () => [h(Input, {
                            autocomplete: 'off',
                            placeholder: '填写单选项问题',
                            value: this.active.input.title,
                            maxlength: 15,
                            "onUpdate:value": v => this.active.input.title = v
                        })]
                    ),
                    h(Flex, {vertical: true, gap: 'middle', style: {maxWidth: '560px'}}, () => [
                        h(Flex, {gap: 'middle', wrap: 'wrap'}, () => this.active.input.items.map((it, i) =>
                            h('div', {
                                    onDragstart: (evt: DragEvent) => this.drag.onDragStart(evt, i),
                                    onDragenter: (evt: DragEvent) => this.drag.onDragEnter(evt, i, this.active.input.items),
                                    onDragover: (evt: DragEvent) => this.drag.onDragOver(evt),
                                    onDragend: (evt: DragEvent) => (this.drag.isDragEnabled = false) && this.drag.onDragEnd(evt),
                                    draggable: true,
                                }, h(Input, {
                                    value: it,
                                    "onUpdate:value": (v: string) => this.active.input.items[i] = v,
                                    maxlength: 15,
                                    placeholder: `填写选项${i + 1}`,
                                    status: this.active.fs.validateInfos[`items.${i}`].validateStatus as "" | "error" | "warning" | undefined
                                }, {
                                    addonBefore: () => h(MenuOutlined, {
                                        style: {cursor: 'move'}, onMousedown: () => this.drag.isDragEnabled = true
                                        , onMouseup: () => this.drag.isDragEnabled = false
                                    }),
                                    addonAfter: () => h(Button, {
                                        type: "link",
                                        danger: true,
                                        disabled: this.active.input.items.length < 3,
                                        onClick: () => this.active.input.items.splice(i, 1)
                                    }, {icon: () => h(DeleteOutlined)})
                                })
                            )
                        )),
                        h(Button, {onClick: () => this.active.input.items.push('')}, {
                            icon: () => h(PlusCircleOutlined),
                            default: () => '添加选项'
                        })
                    ])
                ])
            default:
                return h('div')
        }
    }


    desc(input: IInput): string {
        switch (input.type) {
            case 'input':
                return `填写${({'number': '数字', 'text': '文字', 'date': '日期'} as {
                    [key: string]: string
                })[input.input_type]}`
            case 'checkbox': {
                const cfg = input;
                return `${cfg.items.length}选${cfg.max == cfg.min ? cfg.max : cfg.min + '/' + cfg.max}：${cfg.items.join('，')}`
            }
            case 'radio': {
                return input.items.join('，')
            }
            case 'phone_number':
                return ''
            default:
                return ''
        }
    }

}